import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Product } from './useProducts';

export interface CartItem {
  productId: string;
  productName: string;
  price: number;
  quantity: number;
  stock: number;
  imageUrl: string | null;
  notes: string;
  variantName?: string;
  variantId?: string;
}

interface CartStore {
  items: CartItem[];
  addItem: (product: Product, variant?: { id: string; name: string; price: number; stock: number }) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  incrementQuantity: (productId: string) => void;
  decrementQuantity: (productId: string) => void;
  updateNotes: (productId: string, notes: string) => void;
  clearCart: () => void;
  getSubtotal: () => number;
  getTax: (taxRate: number) => number;
  getTotal: (taxRate: number, taxEnabled: boolean) => number;
  getItemCount: () => number;
  getItemByProductId: (productId: string) => CartItem | undefined;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (product: Product, variant?: { id: string; name: string; price: number; stock: number }) =>
        set((state) => {
          // Create unique key for product + variant combination
          const itemKey = variant ? `${product.id}:${variant.id}` : product.id;
          
          const existingItem = state.items.find(
            (item) => item.productId === itemKey
          );

          const itemPrice = variant ? variant.price : product.price;
          const itemStock = variant ? variant.stock : product.stock;

          if (existingItem) {
            if (existingItem.quantity >= itemStock) {
              return state;
            }
            return {
              items: state.items.map((item) =>
                item.productId === itemKey
                  ? { ...item, quantity: item.quantity + 1 }
                  : item
              ),
            };
          }

          return {
            items: [
              ...state.items,
              {
                productId: itemKey,
                productName: product.name,
                price: itemPrice,
                quantity: 1,
                stock: itemStock,
                imageUrl: product.imageUrl,
                notes: '',
                variantName: variant?.name,
                variantId: variant?.id,
              },
            ],
          };
        }),

      removeItem: (productId: string) =>
        set((state) => ({
          items: state.items.filter((item) => item.productId !== productId),
        })),

      updateQuantity: (productId: string, quantity: number) =>
        set((state) => {
          if (quantity <= 0) {
            return {
              items: state.items.filter(
                (item) => item.productId !== productId
              ),
            };
          }

          return {
            items: state.items.map((item) =>
              item.productId === productId
                ? { ...item, quantity: Math.min(quantity, item.stock) }
                : item
            ),
          };
        }),

      incrementQuantity: (productId: string) =>
        set((state) => ({
          items: state.items.map((item) =>
            item.productId === productId && item.quantity < item.stock
              ? { ...item, quantity: item.quantity + 1 }
              : item
          ),
        })),

      decrementQuantity: (productId: string) =>
        set((state) => {
          const item = state.items.find((i) => i.productId === productId);
          if (!item) return state;

          if (item.quantity <= 1) {
            return {
              items: state.items.filter(
                (i) => i.productId !== productId
              ),
            };
          }

          return {
            items: state.items.map((i) =>
              i.productId === productId
                ? { ...i, quantity: i.quantity - 1 }
                : i
            ),
          };
        }),

      updateNotes: (productId: string, notes: string) =>
        set((state) => ({
          items: state.items.map((item) =>
            item.productId === productId ? { ...item, notes } : item
          ),
        })),

      clearCart: () => set({ items: [] }),

      getSubtotal: () => {
        return get().items.reduce(
          (total, item) => total + item.price * item.quantity,
          0
        );
      },

      getTax: (taxRate: number) => {
        const subtotal = get().getSubtotal();
        return Math.round(subtotal * (taxRate / 100));
      },

      getTotal: (taxRate: number, taxEnabled: boolean) => {
        const subtotal = get().getSubtotal();
        const tax = taxEnabled ? get().getTax(taxRate) : 0;
        return subtotal + tax;
      },

      getItemCount: () => {
        return get().items.reduce((count, item) => count + item.quantity, 0);
      },

      getItemByProductId: (productId: string) => {
        return get().items.find((item) => item.productId === productId);
      },
    }),
    { name: 'cart-storage' }
  )
);
